import React from "react";
import {
  SimpleForm,
  Edit,
  TextInput,
  TextField,
  useTranslate,
  BooleanInput,
  required,
  SaveButton,
  Toolbar,
  FormDataConsumer,
} from "react-admin";
import Decimal from "decimal.js-light";

import CloseIcon from "@mui/icons-material/Close";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";

import Receipt from "./Receipt";
import OrderLink from "./OrderLink";
import TextInputNoWheel from "../Common/TextInputNoWheel";
import CustomDateField from "../Common/CustomDateField";
import { OFFLINE } from "./types";

// ----------- Utils ------------
export const format = (v) => {
  const test = Number(v);
  if (v === "") return "";
  if (test === 0) return v.toString();
  if (!isNaN(test)) return new Decimal(v).dividedBy(100).todp(2).toNumber();
  return "";
};

export const parse = (v) => {
  const test = Number(v);
  if (v === "") return "";
  if (test === 0) return 0;
  if (!isNaN(test)) return new Decimal(v).times(100).toNumber();
  return "";
};

// ----------- Styled Components ------------
const RootWrapper = styled("div")(({ theme }) => ({
  paddingTop: 40,
}));

const TitleWrapper = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  margin: "1em",
}));

const FormWrapper = styled(SimpleForm)(({ theme }) => ({
  [theme.breakpoints.up("xs")]: {
    width: 400,
  },
  [theme.breakpoints.down("xs")]: {
    width: "100vw",
    marginTop: -30,
  },
}));

// ----------- Toolbar Component ------------
const PaymentEditToolbar = (props) => (
  <Toolbar {...props}>
    <SaveButton label="Save" redirect="/payments" />
  </Toolbar>
);

// ----------- Main Component ------------
export const PaymentEdit = (props) => {
  const { onCancel } = props;
  const translate = useTranslate();

  return (
    <RootWrapper>
      <TitleWrapper>
        <Typography variant="h6">{translate("Payment edit")}</Typography>
        <IconButton onClick={onCancel}>
          <CloseIcon />
        </IconButton>
      </TitleWrapper>

      <Edit title="Edit" {...props}>
        <FormWrapper toolbar={<PaymentEditToolbar />}>
          <TextField source="order.customerName" label="Name" />
          <Receipt source="receipt.fileUrl" src="receipt.fileUrl" />

          <FormDataConsumer subscription={{ values: true }}>
            {({ formData, ...rest }) => {
              const isOffline = formData.type === OFFLINE;
              const disabled = !isOffline;
              const label = formData.paid ? "Paid" : "Unpaid";

              return (
                <>
                  <TextInputNoWheel
                    type="number"
                    source="paidAmount"
                    onKeyPress={(e) => {
                      if (e.key === "Enter") e.preventDefault();
                    }}
                    validate={required()}
                    fullWidth
                    format={format}
                    parse={parse}
                    disabled={disabled}
                  />
                  <BooleanInput
                    source="paid"
                    {...rest}
                    label={label}
                    disabled={disabled}
                  />
                  <TextInput source="remarks" disabled={disabled} />
                </>
              );
            }}
          </FormDataConsumer>

          <OrderLink />
          <CustomDateField source="createdAt" showTime />
          <CustomDateField source="updatedAt" showTime />
        </FormWrapper>
      </Edit>
    </RootWrapper>
  );
};

export default PaymentEdit;
